import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Phone,
  Mail,
  MessageSquare,
  Send,
  CheckCircle2,
  X,
  Sparkles,
  HelpCircle,
  Bot,
} from "lucide-react";
import api from "../../utils/apis";
import "./EnquiryModal.css";

const CustomBotIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M 4 11 A 8 8 0 0 1 20 11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <rect x="1.5" y="10" width="3.5" height="8" rx="1.5" />
    <rect x="19" y="10" width="3.5" height="8" rx="1.5" />
    <path d="M 5.5 12 C 5.5 9.5 7.5 7.5 10 7.5 h 4 C 16.5 7.5 18.5 9.5 18.5 12 v 3 C 18.5 17.5 16.5 19.5 14 19.5 h -4 C 7.5 19.5 5.5 17.5 5.5 15 Z" />
    <circle cx="9.5" cy="12.5" r="1.75" fill="#00B2A9" />
    <circle cx="14.5" cy="12.5" r="1.75" fill="#00B2A9" />
    <path d="M 20.5 15 v 2 A 4.5 4.5 0 0 1 16 21.5 h -5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M 11 21.5 h -2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const EnquiryModal = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const handleButtonClick = () => {
    if (window.innerWidth < 768) {
      navigate("/enquiry");
    } else {
      toggleModal();
    }
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      if (isSuccess) {
        resetForm();
      }
      setApiError("");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      message: "",
    });
    setErrors({});
    setApiError("");
    setIsSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    if (apiError) setApiError("");
  };

  const validate = () => {
    const newErrors = {};

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = "Name is required.";
    }

    const trimmedPhone = formData.phone.trim();
    if (!trimmedPhone) {
      newErrors.phone = "Mobile number is required.";
    } else {
      const phoneRegex = /^[0-9+\s\-()]{7,20}$/;
      const digitCount = (trimmedPhone.match(/\d/g) || []).length;
      if (!phoneRegex.test(trimmedPhone) || digitCount < 7 || digitCount > 15) {
        newErrors.phone = "Please enter a valid 10-digit mobile number.";
      }
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      newErrors.email = "Email address is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        newErrors.email = "Please enter a valid email address.";
      }
    }

    const trimmedMessage = formData.message.trim();
    if (!trimmedMessage) {
      newErrors.message = "Message is required.";
    } else if (trimmedMessage.length > 1000) {
      newErrors.message = "Message cannot exceed 1000 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    setApiError("");

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      const response = await api.post("/enquiries", payload);

      if (response.data && response.data.success) {
        setIsSuccess(true);
      } else {
        setApiError(
          response.data?.message || "Failed to submit enquiry. Please try again."
        );
      }
    } catch (err) {
      console.error("Enquiry submission error:", err);
      const serverMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "An unexpected error occurred. Please try again.";

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      setApiError(serverMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (isSuccess) {
      resetForm();
    }
  };

  return (
    <>
      {/* Floating Enquiry Button */}
      <button
        onClick={handleButtonClick}
        className="floating-enquiry-btn group"
        aria-label="Open Enquiry Form"
        type="button"
      >
        <span className="pulse-ring"></span>
        <CustomBotIcon className="w-6 h-6 transition-transform group-hover:rotate-12" />
      </button>

      {/* Enquiry Modal */}
      {isOpen && (
        <div
          className="enquiry-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="enquiry-modal-container" role="dialog" aria-modal="true">
            {/* Modal Header */}
            <div className="enquiry-modal-header">
              <div className="flex items-center gap-3">
                <div className="modal-header-icon-badge">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="flex items-center gap-1.5 text-white font-bold text-lg leading-snug">
                    Send an Enquiry
                    <Sparkles className="w-4 h-4 text-amber-300 fill-amber-300" />
                  </h3>
                  <p className="modal-header-subtitle">
                    Fill out the form below and our medical team will reach out to you.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="enquiry-modal-close-btn"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="enquiry-modal-body">
              {isSuccess ? (
                <div className="success-container">
                  <div className="success-icon-wrapper">
                    <CheckCircle2 className="w-10 h-10 text-[#00B2A9]" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">
                    Enquiry Submitted!
                  </h4>
                  <p className="text-slate-600 mb-6 leading-relaxed text-sm">
                    Thank you for contacting Mexamedico. Our team will review your enquiry and get back to you shortly.
                  </p>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="submit-btn"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {apiError && (
                    <div className="alert-danger" role="alert">
                      <HelpCircle className="w-5 h-5 flex-shrink-0 text-red-600" />
                      <span>{apiError}</span>
                    </div>
                  )}

                  {/* Name Input */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="enquiry-name">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="input-icon-wrapper">
                      <User className="input-field-icon" />
                      <input
                        id="enquiry-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className={`form-input with-icon ${errors.name ? "is-invalid" : ""}`}
                        disabled={submitting}
                        required
                      />
                    </div>
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>

                  {/* Mobile Input */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="enquiry-phone">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="input-icon-wrapper">
                      <Phone className="input-field-icon" />
                      <input
                        id="enquiry-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter 10-digit mobile number"
                        className={`form-input with-icon ${errors.phone ? "is-invalid" : ""}`}
                        disabled={submitting}
                        required
                      />
                    </div>
                    {errors.phone && (
                      <div className="invalid-feedback">{errors.phone}</div>
                    )}
                  </div>

                  {/* Email Input */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="enquiry-email">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="input-icon-wrapper">
                      <Mail className="input-field-icon" />
                      <input
                        id="enquiry-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="name@example.com"
                        className={`form-input with-icon ${errors.email ? "is-invalid" : ""}`}
                        disabled={submitting}
                        required
                      />
                    </div>
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="enquiry-message">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <div className="input-icon-wrapper textarea-icon-wrapper">
                      <MessageSquare className="input-field-icon textarea-icon" />
                      <textarea
                        id="enquiry-message"
                        name="message"
                        rows="3"
                        maxLength={1000}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Write your enquiry details here..."
                        className={`form-textarea with-icon ${errors.message ? "is-invalid" : ""}`}
                        disabled={submitting}
                        required
                      ></textarea>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      {errors.message ? (
                        <div className="invalid-feedback">{errors.message}</div>
                      ) : (
                        <div></div>
                      )}
                      <div className="char-counter">
                        {formData.message.length} / 1000
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="submit-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="spinner"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Enquiry</span>
                        <Send className="w-4 h-4 text-white" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnquiryModal;

